import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { FileText, Upload, Loader2, Check, FolderPlus, Crown, AlertCircle, Image as ImgIcon, BookOpen, X, Sparkles } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

function UpgradeGate() {
  return (
    <div className="p-6 sm:p-8 lg:p-12 max-w-3xl mx-auto" data-testid="slides-upgrade-gate">
      <div className="nb-card p-8 sm:p-10 bg-yellow-50 text-center">
        <div className="w-16 h-16 mx-auto bg-yellow-300 border-2 border-slate-900 rounded-md grid place-items-center" style={{ boxShadow: "4px 4px 0 0 #0F172A" }}>
          <FileText className="w-8 h-8" strokeWidth={2.5}/>
        </div>
        <h1 className="font-display text-3xl sm:text-4xl font-black mt-6">Upgrade to Studict Pro Plus to unlock Slides → Notes</h1>
        <p className="text-base font-medium text-slate-700 mt-4 max-w-xl mx-auto">
          Upload a lecture PDF or PowerPoint. Studict turns every slide into a short summary plus a list of key vocabulary you can send straight to your glossary.
        </p>
        <Link to="/pricing" data-testid="slides-upgrade-cta" className="nb-btn-primary inline-flex items-center gap-2 mt-8">
          <Crown className="w-4 h-4" strokeWidth={2.5}/> See Pro Plus pricing
        </Link>
      </div>
    </div>
  );
}

export default function Slides() {
  const { user } = useAuth();
  const isProPlus = user?.plan === "pro_plus";

  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [includeDeck, setIncludeDeck] = useState(true);
  const [result, setResult] = useState(null);            // {deck_summary, slides[], source_filename}
  const [slideSelected, setSlideSelected] = useState({}); // {"slideIdx-vocabIdx": true}
  const [categories, setCategories] = useState([]);
  const [categoryId, setCategoryId] = useState("");
  const [newCatName, setNewCatName] = useState("");
  const [creatingCat, setCreatingCat] = useState(false);
  const [adding, setAdding] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!isProPlus) return;
    (async () => {
      try {
        const cats = await api.get("/categories");
        setCategories(cats.data || []);
      } catch { /* ignore */ }
    })();
  }, [isProPlus]);

  const onPick = (f) => {
    if (!f) return;
    const ok = /\.(pdf|pptx)$/i.test(f.name);
    if (!ok) { toast.error("Please upload a PDF or PPTX file."); return; }
    if (f.size > 25 * 1024 * 1024) { toast.error("File too large. Max 25 MB."); return; }
    setFile(f);
    setResult(null);
    setSlideSelected({});
  };

  const extract = async () => {
    if (!file) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("include_deck_summary", includeDeck ? "true" : "false");
      const r = await api.post("/slides/extract", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(r.data);
      // Default: tick every vocab item
      const s = {};
      (r.data.slides || []).forEach((sl) => {
        (sl.vocab || []).forEach((_, vi) => { s[`${sl.index}-${vi}`] = true; });
      });
      setSlideSelected(s);
      const totalVocab = (r.data.slides || []).reduce((a, sl) => a + (sl.vocab?.length || 0), 0);
      toast.success(`${r.data.slide_count} slides processed · ${totalVocab} vocab terms found.`);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 403) toast.error(detail || "This is a Pro Plus feature.");
      else if (status === 413) toast.error(detail || "File too large.");
      else if (status === 400) toast.error(detail || "Couldn't read this file.");
      else toast.error(detail || "Slides processing failed.");
    } finally {
      setLoading(false);
    }
  };

  const createCategory = async () => {
    const name = newCatName.trim();
    if (!name) return;
    setCreatingCat(true);
    try {
      const r = await api.post("/categories", { name });
      setCategories((c) => [...c, r.data]);
      setCategoryId(r.data.id);
      setNewCatName("");
      toast.success(`Category "${name}" created.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't create category.");
    } finally { setCreatingCat(false); }
  };

  const pickedVocab = () => {
    const out = [];
    (result?.slides || []).forEach((sl) => {
      (sl.vocab || []).forEach((v, vi) => {
        if (slideSelected[`${sl.index}-${vi}`]) out.push({ term: v.term, definition: v.definition });
      });
    });
    return out;
  };

  const addAll = async () => {
    const items = pickedVocab();
    if (items.length === 0) { toast.error("Tick at least one term."); return; }
    setAdding(true);
    try {
      const r = await api.post("/words/bulk", { words: items, category_id: categoryId || null });
      const added = r.data.added || 0;
      const skipped = r.data.skipped_duplicates || 0;
      if (added > 0) toast.success(`Added ${added} word${added === 1 ? "" : "s"} to your glossary${skipped ? ` — ${skipped} duplicates skipped.` : "."}`);
      else if (skipped > 0) toast.message(`All ${skipped} words were already in your glossary.`);
      // Untick the just-added ones
      const next = { ...slideSelected };
      (result?.slides || []).forEach((sl) => {
        (sl.vocab || []).forEach((_, vi) => { if (next[`${sl.index}-${vi}`]) next[`${sl.index}-${vi}`] = false; });
      });
      setSlideSelected(next);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't add to glossary.");
    } finally { setAdding(false); }
  };

  const removeSlide = (idx) => {
    setResult((prev) => prev ? { ...prev, slides: prev.slides.filter((s) => s.index !== idx) } : prev);
  };

  if (!isProPlus) return <UpgradeGate />;

  const totalSelected = Object.values(slideSelected).filter(Boolean).length;

  return (
    <div className="p-6 sm:p-8 lg:p-12 max-w-6xl" data-testid="slides-page">
      <header className="mb-6">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Pro Plus</p>
        <h1 className="font-display text-4xl font-black mt-1 flex items-center gap-2">
          <FileText className="w-8 h-8" strokeWidth={2.5}/> Slides → Notes
        </h1>
        <p className="text-base font-medium text-slate-600 mt-2">
          Drop a lecture PDF or PowerPoint. We turn every slide into a short summary plus key vocab — straight into your glossary.
        </p>
      </header>

      {/* Uploader */}
      <div className="grid lg:grid-cols-[1fr_360px] gap-5">
        <label
          htmlFor="slides-file"
          data-testid="slides-dropzone"
          className="block cursor-pointer border-4 border-dashed border-slate-900 rounded-lg p-10 text-center transition-colors hover:bg-sky-100"
          style={{ background: "#7DD3FC33" }}
        >
          {file ? (
            <div>
              <FileText className="w-10 h-10 mx-auto" strokeWidth={2.5}/>
              <p className="font-display text-xl font-bold mt-3 truncate" title={file.name}>{file.name}</p>
              <p className="text-xs text-slate-600 mt-1">{(file.size / 1024 / 1024).toFixed(1)} MB · click to choose another</p>
            </div>
          ) : (
            <>
              <Upload className="w-10 h-10 mx-auto" strokeWidth={2.5}/>
              <p className="font-display text-xl font-bold mt-3">Upload a lecture deck</p>
              <p className="text-sm text-slate-600 mt-1">PDF or PPTX · max 60 slides · up to 25 MB</p>
            </>
          )}
          <input
            ref={inputRef}
            id="slides-file"
            data-testid="slides-file-input"
            type="file"
            accept=".pdf,.pptx,application/pdf,application/vnd.openxmlformats-officedocument.presentationml.presentation"
            className="hidden"
            onChange={(e) => onPick(e.target.files?.[0])}
          />
        </label>

        <div className="nb-card p-4 bg-white space-y-3">
          <label className="flex items-start gap-2 cursor-pointer" data-testid="slides-deck-summary-toggle">
            <input
              type="checkbox"
              checked={includeDeck}
              onChange={(e) => setIncludeDeck(e.target.checked)}
              className="mt-0.5 w-4 h-4 border-2 border-slate-900 rounded shrink-0"
            />
            <span className="text-sm">
              <strong className="block">Also write a whole-deck summary</strong>
              <span className="text-slate-600 text-xs">A 5-7 sentence overview of the entire lecture so you can revise the big picture.</span>
            </span>
          </label>
          <button
            onClick={extract}
            disabled={!file || loading}
            data-testid="slides-extract-btn"
            className="nb-btn-primary w-full inline-flex items-center justify-center gap-2"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2.5}/> : <Sparkles className="w-4 h-4" strokeWidth={2.5}/>}
            {loading ? "Processing… this can take 20–60s" : "Turn into notes"}
          </button>
          {file && (
            <button
              onClick={() => { setFile(null); setResult(null); if (inputRef.current) inputRef.current.value = ""; }}
              className="text-xs font-bold underline hover:no-underline w-full text-center"
            >
              Reset
            </button>
          )}
        </div>
      </div>

      {/* Results */}
      {result && (
        <div className="mt-8 space-y-5" data-testid="slides-result">
          {result.deck_summary && (
            <div className="nb-card p-5 bg-yellow-50" data-testid="slides-deck-summary">
              <h2 className="font-display text-xl font-black flex items-center gap-2">
                <BookOpen className="w-5 h-5" strokeWidth={2.5}/> Whole-deck summary
              </h2>
              <p className="text-sm text-slate-800 mt-2 leading-relaxed whitespace-pre-wrap">{result.deck_summary}</p>
            </div>
          )}

          {/* Category picker + Add button (sticky) */}
          <div className="nb-card p-4 bg-emerald-50 sticky top-2 z-10" data-testid="slides-add-bar">
            <div className="grid sm:grid-cols-[1fr_auto] gap-3 items-end">
              <div>
                <label className="text-xs font-bold uppercase tracking-widest text-slate-700 flex items-center gap-1.5">
                  <FolderPlus className="w-3.5 h-3.5" strokeWidth={2.5}/> Add ticked vocab to category
                </label>
                <div className="flex gap-2 mt-1.5">
                  <select
                    value={categoryId}
                    onChange={(e) => setCategoryId(e.target.value)}
                    data-testid="slides-category-select"
                    className="nb-input flex-1 min-w-[160px]"
                  >
                    <option value="">Uncategorised</option>
                    {categories.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
                <div className="flex gap-2 mt-2">
                  <input
                    type="text"
                    placeholder="…or create a new category (e.g. Biology — Lecture 4)"
                    value={newCatName}
                    onChange={(e) => setNewCatName(e.target.value)}
                    data-testid="slides-new-category-input"
                    className="nb-input flex-1 text-sm"
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); createCategory(); } }}
                  />
                  <button onClick={createCategory} disabled={!newCatName.trim() || creatingCat} className="nb-btn-secondary text-sm whitespace-nowrap">
                    {creatingCat ? "Creating…" : "Create"}
                  </button>
                </div>
              </div>
              <button
                onClick={addAll}
                disabled={adding || totalSelected === 0}
                data-testid="slides-add-all-btn"
                className="nb-btn-primary inline-flex items-center justify-center gap-2 !py-3 self-start sm:self-end"
              >
                <Check className="w-4 h-4" strokeWidth={2.5}/>
                {adding ? "Adding…" : `Add ${totalSelected} to glossary`}
              </button>
            </div>
          </div>

          {/* Per-slide cards */}
          <div className="space-y-4">
            {result.slides.map((sl) => (
              <article key={sl.index} className="nb-card p-5 bg-white" data-testid={`slide-card-${sl.index}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-bold uppercase tracking-widest text-slate-500">Slide {sl.index}</p>
                    <h3 className="font-display text-lg font-black mt-0.5">{sl.title}</h3>
                  </div>
                  <button
                    onClick={() => removeSlide(sl.index)}
                    data-testid={`slide-remove-${sl.index}`}
                    title="Remove this slide from results"
                    className="shrink-0 p-1.5 border-2 border-slate-900 rounded-md bg-white hover:bg-red-50"
                  >
                    <X className="w-3.5 h-3.5" strokeWidth={2.5}/>
                  </button>
                </div>

                {sl.image_only && (
                  <div className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider bg-slate-200 text-slate-700 px-1.5 py-0.5 rounded">
                    <ImgIcon className="w-3 h-3" strokeWidth={2.5}/> Image / diagram slide
                  </div>
                )}

                {sl.summary && (
                  <p className="text-sm text-slate-700 mt-2 leading-relaxed whitespace-pre-wrap">{sl.summary}</p>
                )}

                {sl.vocab.length > 0 && (
                  <div className="mt-3 pt-3 border-t-2 border-slate-100">
                    <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">Vocab from this slide</p>
                    <ul className="space-y-2">
                      {sl.vocab.map((v, vi) => {
                        const key = `${sl.index}-${vi}`;
                        return (
                          <li key={key} className="flex items-start gap-3" data-testid={`slide-${sl.index}-vocab-${vi}`}>
                            <input
                              type="checkbox"
                              checked={!!slideSelected[key]}
                              onChange={(e) => setSlideSelected((s) => ({ ...s, [key]: e.target.checked }))}
                              className="mt-1 w-5 h-5 border-2 border-slate-900 accent-yellow-300"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="font-display font-bold">{v.term}</div>
                              <div className="text-sm text-slate-700 whitespace-pre-wrap">{v.definition}</div>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}

                {sl.vocab.length === 0 && !sl.image_only && !sl.summary && (
                  <p className="text-xs text-slate-500 mt-2 inline-flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" strokeWidth={2.5}/> Skipped — looks like a title/agenda/decorative slide.
                  </p>
                )}
              </article>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
